import { Dialog } from "@headlessui/react"
import { FC, useEffect, useState } from "react"
import {
  ANALYSIS_FILTER,
  errorFilledToastOptions,
  PAYMENT_PERIOD_TYPE,
  PAYMENT_PERIOD_TYPE_ID,
  tableCellClasses
} from "../../../../../constants"
import { X } from "heroicons-react"
import * as React from "react"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { userPayments } from "../../../../../apis"
import { toast } from "react-toastify"
import { getDaysInMonth, getPaymentPeriodOptions, mapListToCamelCase } from "../../../../../utility"
import Payment from "../../../account/payment/Payment"
import { RootState } from "../../../../../store"
import { connect } from "react-redux"
import { paymentMethodsAdminEntry } from "../../../../../actions/adminActions"
import UserContract from "../../../account/user-contract/UserContract"

type ApprovePaymentModalProps = {
  selectedIds: number[]
  open: boolean
  userPaymentPeriodId: number | undefined
  selectedPeriod: string
  selectedUsername?: string
  selectedUserId?: number
  onClose: () => void
  approve: (
    paymentDay: string,
    selectedDate: string[],
    deductFromAdvance: boolean,
    approveAll: boolean,
    payWithRevolut: boolean,
    sendEmail: boolean,
    bankFee: string
  ) => void
  selectedFilter: string
  selectedPaymentUser?: KSelectOption | undefined
  paymentMethodsAdminEntry: (adminPanelUserId?: number) => void
}

const quarterMap = {
  "01": "q1",
  "02": "q2",
  "03": "q2",
  "04": "q2",
  "05": "q3",
  "06": "q3",
  "07": "q3",
  "08": "q4",
  "09": "q4",
  10: "q4",
  11: "q1",
  12: "q1"
}

const monthToQuarter = {
  q1: ["11", "12", "01"],
  q2: ["02", "03", "04"],
  q3: ["05", "06", "07"],
  q4: ["08", "09", "10"]
}
const getNormalizedSelectedDate = (selectedPeriod: string, userPaymentPeriodId?: number) => {
  if (userPaymentPeriodId === PAYMENT_PERIOD_TYPE_ID.QUARTERLY && selectedPeriod.length === 6) {
    const month = selectedPeriod.substring(4, 6)
    let year = Number(selectedPeriod.substring(0, 4))

    const quarter = quarterMap[month]

    // FIX: Nov/Dec belong to NEXT year's Q1
    if (quarter === "q1" && (month === "11" || month === "12")) {
      year += 1
    }

    return `${year}${quarter}`
  }

  return selectedPeriod
}
const getDefaultTransferDate = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const lastDay = getDaysInMonth(year, month - 1)

  return `${year}${String(month).padStart(2, "0")}${String(lastDay).padStart(2, "0")}`
}

const formatMoneySendDate = (dateValue: string) => {
  if (dateValue.length !== 8) return dateValue

  const year = Number(dateValue.substring(0, 4))
  const month = Number(dateValue.substring(4, 6))
  const day = Number(dateValue.substring(6, 8))
  const date = new Date(year, month - 1, day)

  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
}

const getTodayDate = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const day = now.getDate()

  return `${year}${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`
}

interface PaymentAmounts {
  advancePayment: number
  regularPayment: number
  currency: string
}

const ApprovePaymentModal: FC<ApprovePaymentModalProps> = (props) => {
  const { onClose, open, selectedIds } = props
  const [dateList, setDateList] = useState<KSelectOption[]>([])
  const [selectedDate, setSelectedDate] = useState<string>(
    getNormalizedSelectedDate(props.selectedPeriod, props.userPaymentPeriodId)
  )
  const [paymentDay, setPaymentDay] = useState<string>("")
  const defaultValueForDeduction = false
  const [deductFromAdvance, setDeductFromAdvance] = useState<boolean>(defaultValueForDeduction)
  const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmounts[]>([])
  const [isDeductionPossible, setIsDeductionPossible] = useState<boolean>(false)
  const [approveAll, setApproveAll] = useState(false)
  const [payWithRevolut, setPayWithRevolut] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)
  const [bankFee, setBankFee] = useState<string>("")

  const moneySendDate = getDefaultTransferDate()

  useEffect(() => {
    setDateList(getPaymentPeriodOptions(props.userPaymentPeriodId))
  }, [props.userPaymentPeriodId])

  useEffect(() => {
    const isUserBalance = props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
    props.paymentMethodsAdminEntry(isUserBalance ? props.selectedPaymentUser?.value : props.selectedUserId)
  }, [props.selectedUserId, props.selectedPaymentUser])

  const calculatePaymentAmounts = async () => {
    const isUserBalance = props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
    if (isUserBalance && !props.selectedPaymentUser) {
      return
    }

    if (!isUserBalance && !props.selectedUserId) {
      return
    }

    const date: string[] = createDateArray()
    const requestBody = {
      date,
      user_id: isUserBalance ? props.selectedPaymentUser?.value : props.selectedUserId,
      payment_day: paymentDay
    }
    try {
      const userBalanceParam = isUserBalance && approveAll ? "&approve_all=1" : ""
      const selectedIdsParam = selectedIds?.length > 0 ? `&selected_ids=${selectedIds.join(",")}` : ""
      const response = await userPayments(
        `/calculate_payment_amounts/?t=1${selectedIdsParam}${userBalanceParam}`,
        "post",
        requestBody
      )
      if (response.status === 200) {
        const calculatedPaymentAmounts: PaymentAmounts[] = mapListToCamelCase(response.data.results)

        const deductionPossible = calculatedPaymentAmounts.some((paymentAmount) => paymentAmount.advancePayment > 0)

        setPaymentAmounts(calculatedPaymentAmounts)
        setIsDeductionPossible(deductionPossible)
      } else {
        toast.error("Error on calculations", errorFilledToastOptions)
      }
    } catch (err) {
      toast.error("Error on calculations", errorFilledToastOptions)
    }
  }

  useEffect(() => {
    if (open && paymentDay.length === 8) {
      calculatePaymentAmounts()
    }
  }, [selectedDate, open, paymentDay, approveAll])

  const resetSelections = () => {
    const normalizedSelectedDate = getNormalizedSelectedDate(props.selectedPeriod, props.userPaymentPeriodId)

    setDeductFromAdvance(defaultValueForDeduction)
    setPayWithRevolut(false)
    setSendEmail(false)
    setPaymentDay("")
    setSelectedDate(normalizedSelectedDate)
    setBankFee("")
    setApproveAll(false)
  }

  useEffect(() => {
    if (!props.userPaymentPeriodId) {
      return
    }

    resetSelections()
  }, [props.selectedPeriod, props.userPaymentPeriodId])

  const createDateArray = () => {
    const date: string[] = []
    if (selectedDate.includes("q")) {
      const year = Number(selectedDate.substring(0, 4))
      const quarter = selectedDate.substring(4, 6)

      const months = monthToQuarter[quarter] || []

      months.forEach((month) => {
        const isQ1Carry = quarter === "q1" && (month === "11" || month === "12")
        const finalYear = isQ1Carry ? year - 1 : year
        date.push(`${finalYear}${month}`)
      })

      return date
    }

    return [selectedDate]
  }

  const createReportingPeriodArray = () => {
    const activityPeriodArray = createDateArray()
    return activityPeriodArray.map((yyyymm) => {
      const year = Number(yyyymm.slice(0, 4))
      const month = Number(yyyymm.slice(4, 6)) - 1 // JS months are 0-based

      const date = new Date(year, month)
      const monthsToAdd = Number(2) // For Reporting Period. Add 2 months
      date.setMonth(date.getMonth() + monthsToAdd)

      const newYear = date.getFullYear()
      const newMonth = String(date.getMonth() + 1).padStart(2, "0")

      return `${newYear}${newMonth}`
    })
  }

  const createPaymentPeriodArray = () => {
    const activityPeriodArray = createDateArray()
    return activityPeriodArray.map((yyyymm) => {
      const year = Number(yyyymm.slice(0, 4))
      const month = Number(yyyymm.slice(4, 6)) - 1 // JS months are 0-based

      const date = new Date(year, month)
      const monthsToAdd = Number(3) // For Payment Period. Add 3 months
      date.setMonth(date.getMonth() + monthsToAdd)

      const newYear = date.getFullYear()
      const newMonth = String(date.getMonth() + 1).padStart(2, "0")

      return `${newYear}${newMonth}`
    })
  }

  const approvePayment = () => {
    const date: string[] = createDateArray()
    props.approve(paymentDay, date, deductFromAdvance, approveAll, payWithRevolut, sendEmail, bankFee)
    resetSelections()
  }

  const onCloseClick = () => {
    props.onClose()
    resetSelections()
  }

  const onApproveClick = () => {
    approvePayment()
    onClose()
  }

  const renderUserContract = () => {
    const isUserBalance = props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
    const userId = isUserBalance ? props.selectedPaymentUser?.value : props.selectedUserId

    return (
      <div
        style={{
          borderLeft: "1px solid #11111120",
          minWidth: 360,
          overflowY: "scroll",
          maxHeight: "80vh",
          maxWidth: "80vh"
        }}
      >
        {userId && (
          <div className="px-4 pt-4">
            <UserContract viewOnly={true} userId={userId} />
          </div>
        )}

        <div className="px-4 pt-4">
          <Payment adminPage={true} readOnly={true} />
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onClose={onCloseClick} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 rounded-lg">
        <div className="bg-white elev-b-sm rounded-lg" style={{ minWidth: "470px" }}>
          <div
            className={`bg-gray-50 text-xs text-gray-500 font-medium rounded-t-lg
              uppercase border-b flex justify-between ${tableCellClasses.sm}`}
          >
            <KTitleSpan
              text="APPROVE PAYMENT"
              color="#111"
              fontWeight={500}
              fontSize={14}
              lineHeight="20px"
              letterSpacing="0.2px"
            />
            <X className="h-4 mr-[-6px] cursor-pointer" onClick={onCloseClick} />
          </div>
          <div className="flex">
            <div>
              <div className="px-4 pt-4">
                <div className="flex flex-row gap-1 mb-6">
                  <KSpan text="Selected User:" color="#000" fontSize={16} lineHeight="20px" />
                  <KSpan
                    text={
                      props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
                        ? props.selectedPaymentUser?.label || ""
                        : props.selectedUsername || ""
                    }
                    color="#000"
                    fontWeight={600}
                    fontSize={16}
                    lineHeight="20px"
                  />
                </div>
                <KSpan text="Payment Day (YYYYMMDD) - Accounting Period" color="#000" fontSize={16} lineHeight="20px" />

                <div className="mt-2 flex gap-3 items-center">
                  <div className="flex-1">
                    <KInput
                      dataTestId="approve-payment-modal-payment-day-input"
                      value={paymentDay}
                      placeholder={"Ex: 20230123"}
                      padding="14px"
                      onChange={(text) => {
                        if (text.length <= 8) {
                          setPaymentDay(text)
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="h-[52px] px-4 rounded-[12px] border border-[#E5E7EB] bg-white text-[#111] font-medium whitespace-nowrap"
                    onClick={() => setPaymentDay(getTodayDate())}
                  >
                    Today's Date
                  </button>
                </div>
              </div>
              {paymentDay.length === 8 && (
                <div className="px-4">
                  {isDeductionPossible && (
                    <div className="mt-8 flex flex-row gap-2 items-start max-w-[320px]">
                      <input
                        type="checkbox"
                        value=""
                        className="mt-1 cursor-pointer"
                        checked={deductFromAdvance}
                        style={{
                          accentColor: "#000"
                        }}
                        onChange={() => {
                          setDeductFromAdvance(!deductFromAdvance)
                        }}
                      />
                      <KSpan text="Deduct from advance" color="#000" fontSize={16} lineHeight="20px" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1 mt-8">
                    {paymentAmounts.length > 0 ? (
                      paymentAmounts.map((paymentAmount, index) => {
                        return (
                          <div key={`payment-amount-${index}`} className="flex items-center gap-2">
                            {isDeductionPossible && (
                              <div className="flex flex-row gap-1">
                                <KSpan
                                  text={`Advance Deduction:`}
                                  color="#000"
                                  fontWeight={600}
                                  fontSize={14}
                                  lineHeight="20px"
                                />
                                <KSpan
                                  text={`${deductFromAdvance ? paymentAmount.advancePayment.toFixed(3) : "0"} ${
                                    paymentAmount.currency
                                  },`}
                                  color="#000"
                                  fontWeight={400}
                                  fontSize={14}
                                  lineHeight="20px"
                                />
                              </div>
                            )}
                            <div className="flex flex-row gap-1">
                              <KSpan
                                text={`Payout Amount:`}
                                color="#000"
                                fontWeight={600}
                                fontSize={14}
                                lineHeight="20px"
                              />
                              <KSpan
                                text={` ${
                                  deductFromAdvance
                                    ? paymentAmount.regularPayment.toFixed(3)
                                    : (paymentAmount.regularPayment + paymentAmount.advancePayment).toFixed(3)
                                } ${paymentAmount.currency} `}
                                color="#000"
                                fontWeight={400}
                                fontSize={14}
                                lineHeight="20px"
                              />
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div className="flex flex-row gap-1">
                        <KSpan text={`Payout Amount:`} color="#000" fontWeight={600} fontSize={14} lineHeight="20px" />
                        <KSpan text={`0`} color="#000" fontWeight={400} fontSize={14} lineHeight="20px" />
                      </div>
                    )}
                  </div>

                  <div className="mt-8 mb-2 flex justify-between">
                    <div className=" flex flex-row gap-1 items-center">
                      <KSpan text=" Payment Period:" color="#000" fontSize={16} lineHeight="20px" />
                      {selectedIds.length > 0 ? (
                        <KSpan
                          text={`Selected ${selectedIds.length}`}
                          color="#000"
                          fontWeight={600}
                          fontSize={16}
                          lineHeight="20px"
                        />
                      ) : (
                        <KSpan
                          text={
                            props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE && approveAll
                              ? "ALL"
                              : props.userPaymentPeriodId
                              ? PAYMENT_PERIOD_TYPE[props.userPaymentPeriodId]
                              : "-"
                          }
                          color="#000"
                          fontWeight={600}
                          fontSize={16}
                          lineHeight="20px"
                        />
                      )}
                    </div>
                    <div>
                      <KSpan text="Bank Fee" color="#000" fontSize={16} />
                    </div>
                  </div>
                  <div className="mb-6 flex flex-col gap-3">
                    <div className="flex gap-2 justify-between items-center">
                      <div>
                        {!approveAll && selectedIds.length === 0 && (
                          <KDropdown
                            width={200}
                            padding="14px"
                            defaultValuePrimitive={selectedDate}
                            rightIcon="/analytics_icons/caret-down-new.svg"
                            enableIndicator={true}
                            options={dateList}
                            onSelect={(selectedOption) => {
                              const selected = selectedOption as KSelectOption
                              if (!selected.value2) return
                              setSelectedDate(selected.value2)
                            }}
                            selected={dateList.find((option) => option.value2 === selectedDate)}
                          />
                        )}
                      </div>
                      <div>
                        <KInput height={32} width={52} value={bankFee} onChange={(value) => setBankFee(value)} />
                      </div>
                    </div>
                    {selectedIds.length === 0 && !approveAll && (
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-2">
                          <KSpan text="Activity Period: " />
                          <KSpan text={`(${createDateArray().join(", ")})`} fontSize={13} />
                        </div>
                        <div className="flex gap-2">
                          <KSpan text="Reporting Period: " />
                          <KSpan text={`(${createReportingPeriodArray().join(", ")})`} fontSize={13} />
                        </div>
                        <div className="flex gap-2">
                          <KSpan text="Payment Period: " />
                          <KSpan text={`(${createPaymentPeriodArray().join(", ")})`} fontSize={13} />
                        </div>
                      </div>
                    )}
                  </div>
                  {props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
                    <div className="flex gap-2 items-center">
                      <input
                        data-testid="approve-payment-modal-approve-all-checkbox"
                        type="checkbox"
                        style={{ accentColor: "black" }}
                        onChange={() => setApproveAll(!approveAll)}
                        checked={approveAll}
                      />
                      <KSpan text="Approve ALL" />
                    </div>
                  )}
                  <div className="flex gap-2 items-center mt-4">
                    <input
                      type="checkbox"
                      style={{ accentColor: "black" }}
                      onChange={() => setSendEmail(!sendEmail)}
                      checked={sendEmail}
                    />
                    <KSpan text="Send Email" />
                  </div>
                  <div className="flex gap-2 items-center mt-4">
                    <input
                      type="checkbox"
                      style={{ accentColor: "black" }}
                      onChange={() => setPayWithRevolut(!payWithRevolut)}
                      checked={payWithRevolut}
                    />
                    <KSpan text="Pay with Revolut" />
                  </div>
                  {payWithRevolut && (
                    <div className="mt-4">
                      <KSpan text="Money Send Date" color="#000" fontSize={16} lineHeight="20px" />
                      <div className="mt-2 px-[14px] py-[14px] rounded-[12px] border border-[#E5E7EB] bg-[#F9FAFB]">
                        <KSpan text={formatMoneySendDate(moneySendDate)} color="#111" fontSize={16} lineHeight="24px" />
                      </div>
                      <div className="mt-2">
                        <KSpan
                          text="Defaults to the last day of the current month."
                          color="#666"
                          fontSize={13}
                          lineHeight="18px"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {paymentDay.length === 8 && renderUserContract()}
          </div>
          <div className="flex gap-4 p-4 px-8 mt-3">
            <KButton
              dataTestId="approve-payment-modal-cancel-button"
              text={"Cancel"}
              background="#F7F7F7"
              textColor="#000"
              height="48px"
              padding="14px"
              onClick={onCloseClick}
            />
            <KButton
              dataTestId="approve-payment-modal-approve-button"
              text={"Approve"}
              background="#000"
              textColor="#FFF"
              height="48px"
              disabled={paymentDay.length !== 8}
              padding="14px"
              onClick={onApproveClick}
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

const mapStateToProps = (state: RootState) => {
  return {}
}

export default connect(mapStateToProps, {
  paymentMethodsAdminEntry
})(ApprovePaymentModal)
